import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, useTheme, useMediaQuery, InputAdornment, CircularProgress, Tooltip, Alert, Fade, Autocomplete, ListItemText, Paper, InputBase, Divider } from '@mui/material';
import { XIcon, AlertCircleIcon, CheckCircleIcon, SearchIcon } from 'lucide-react';
import { VALID_SUB_AGENTS, type ValidSubAgent } from '../utils/constants';
// Pakistani phone format for validation
const PAKISTAN_PHONE_FORMAT = {
  prefix: '+92',
  flag: '🇵🇰',
  phoneFormat: '(+92) 3XX XXXXXXX',
  regex: /^\+92\d{10}$/
};
interface SubAgentFormProps {
  open: boolean;
  subAgent: any | null;
  onClose: () => void;
  onSave: (subAgentData: any) => void;
}
interface FormData {
  name: ValidSubAgent | string;
  description: string;
  headOfficeAddress: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  settlementAccount: string;
  accountTitle?: string;
}
interface FormErrors {
  name?: string;
  description?: string;
  headOfficeAddress?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  settlementAccount?: string;
}
// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// IBAN validation regex
const GENERAL_IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const PAKISTAN_IBAN_REGEX = /^PK\d{2}[A-Z0-9]{20}$/;
const SubAgentFormDrawer: React.FC<SubAgentFormProps> = ({
  open,
  subAgent,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    headOfficeAddress: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    settlementAccount: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showAccountTitle, setShowAccountTitle] = useState(false);
  const [ibanValid, setIbanValid] = useState(false);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (subAgent) {
      setFormData({
        name: subAgent.name || '',
        description: subAgent.description || '',
        headOfficeAddress: subAgent.headOfficeAddress || '',
        contactPerson: subAgent.contactPerson || '',
        contactPhone: subAgent.contactPhone || '',
        contactEmail: subAgent.contactEmail || '',
        settlementAccount: subAgent.settlementAccount || '',
        accountTitle: subAgent.accountTitle || ''
      });
      // If subAgent has account title, show the field
      if (subAgent.accountTitle) {
        setShowAccountTitle(true);
        validateIban(subAgent.settlementAccount || '');
      }
    } else {
      setFormData({
        name: '',
        description: '',
        headOfficeAddress: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        settlementAccount: '',
        accountTitle: ''
      });
      setShowAccountTitle(false);
    }
    setErrors({});
    setFetchError(null);
  }, [subAgent, open]);
  // Effect to prefill Pakistan prefix when phone field is empty
  useEffect(() => {
    if (!formData.contactPhone || formData.contactPhone === '' || formData.contactPhone.startsWith('+') && formData.contactPhone.length <= 4) {
      setFormData(prev => ({
        ...prev,
        contactPhone: PAKISTAN_PHONE_FORMAT.prefix + ' '
      }));
    }
  }, [open]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      // Special handling for settlementAccount (IBAN)
      if (name === 'settlementAccount') {
        // Format IBAN: remove spaces/dashes, convert to uppercase
        const formattedValue = String(value).replace(/[\s-]/g, '').toUpperCase();
        setFormData({
          ...formData,
          [name]: formattedValue,
          // Clear account title if IBAN is edited after a successful fetch
          ...(showAccountTitle && {
            accountTitle: ''
          })
        });
        // Validate the IBAN and update the button state
        validateIban(formattedValue);
        // If user changes IBAN after fetching title, reset the title
        if (showAccountTitle) {
          setShowAccountTitle(false);
          setFetchError(null);
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const handlePhoneFocus = () => {
    setPhoneFieldFocused(true);
    // Prefill Pakistan prefix if field is empty
    if (!formData.contactPhone || formData.contactPhone === '') {
      setFormData({
        ...formData,
        contactPhone: PAKISTAN_PHONE_FORMAT.prefix + ' '
      });
    }
  };
  const handlePhoneBlur = () => {
    setPhoneFieldFocused(false);
    // Validate phone number when field loses focus
    validatePhoneNumber(formData.contactPhone);
  };
  const validatePhoneNumber = (phone: string): boolean => {
    // Remove spaces for validation
    const cleanPhone = phone.replace(/\s+/g, '');
    // Check if it's a valid Pakistani phone number
    const isValid = PAKISTAN_PHONE_FORMAT.regex.test(cleanPhone);
    if (!isValid && cleanPhone) {
      setErrors({
        ...errors,
        contactPhone: 'Must start with +92 and be exactly 10 digits after +92'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.contactPhone;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const validateIban = (iban: string): boolean => {
    // Check if it's a valid IBAN format
    const isGeneralValid = GENERAL_IBAN_REGEX.test(iban);
    // If it starts with PK, apply the Pakistan-specific rules
    const isPakistanIban = iban.startsWith('PK');
    const isPakistanValid = isPakistanIban ? PAKISTAN_IBAN_REGEX.test(iban) : true;
    const isValid = isGeneralValid && isPakistanValid;
    setIbanValid(isValid);
    // Set error message if invalid
    if (iban && !isValid) {
      setErrors({
        ...errors,
        settlementAccount: isPakistanIban ? 'Pakistan IBAN must be exactly 24 characters (PK + 22 characters)' : 'Enter a valid IBAN (e.g., PK36BIPL...)'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.settlementAccount;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const handleFetchTitle = () => {
    setIsFetchingTitle(true);
    setFetchError(null);
    // Simulate API call with timeout
    setTimeout(() => {
      // 90% success rate for demo purposes
      const isSuccess = Math.random() < 0.9;
      if (isSuccess) {
        // Success case
        const mockTitles = ['ISLAMABAD EXCHANGE CO.', 'KARACHI MONEY TRANSFER', 'LAHORE FINANCIAL SERVICES', 'PESHAWAR REMIT LLC', 'QUETTA EXCHANGE SERVICES'];
        const randomTitle = mockTitles[Math.floor(Math.random() * mockTitles.length)];
        setFormData({
          ...formData,
          accountTitle: randomTitle
        });
        setShowAccountTitle(true);
        // Show toast (in a real app, you would use a toast library)
        console.log('Account title fetched successfully');
      } else {
        // Error case
        setFetchError('Title fetch failed. Please verify IBAN or try again.');
      }
      setIsFetchingTitle(false);
    }, 2500); // 2.5 seconds delay
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Sub-Agent name is required';
    }
    if (!formData.headOfficeAddress.trim()) {
      newErrors.headOfficeAddress = 'Head Office Address is required';
    }
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact Person is required';
    }
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Contact Phone is required';
    } else {
      // Validate phone number
      const isValid = validatePhoneNumber(formData.contactPhone);
      if (!isValid) {
        // Error message is already set in validatePhoneNumber
        newErrors.contactPhone = newErrors.contactPhone || errors.contactPhone;
      }
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact Email is required';
    } else if (!EMAIL_REGEX.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Contact Email is invalid';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement Account is required';
    } else if (!validateIban(formData.settlementAccount)) {
      newErrors.settlementAccount = formData.settlementAccount.startsWith('PK') ? 'Pakistan IBAN must be exactly 24 characters (PK + 22 characters)' : 'Enter a valid IBAN (e.g., PK36BIPL...)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: subAgent?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-2267" data-mp="32azzxegs1zjc">
      <Box sx={{
      p: 3
    }} data-id="element-2268" data-mp="15gdbhduj0p98">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2269" data-mp="15fu42r2i5j9g">
          <Typography variant="h6" data-id="element-2270" data-mp="2pw5z3kq4c365">
            {subAgent ? 'Edit Sub Agent' : 'Add New Sub Agent'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-2271" data-mp="2c5sp7cto2ee6">
            <XIcon size={18} data-id="element-2272" data-mp="2h29ewbgil0td" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-2273" data-mp="34sn8i6eqz7q2">
          <FormControl fullWidth margin="normal" error={!!errors.name} required data-id="element-2274" data-mp="38sxdbfqszs3c">
            <InputLabel id="sub-agent-name-label" data-id="element-2275" data-mp="2uv8sg7wqkkya">Sub-Agent Name</InputLabel>
            <Select labelId="sub-agent-name-label" name="name" value={formData.name} onChange={handleChange} label="Sub-Agent Name" data-id="element-2276" data-mp="2ztaaqqmc8g8p">
              {VALID_SUB_AGENTS.map(subAgent => <MenuItem key={subAgent} value={subAgent} data-id="element-2277" data-mp="1ocaifuzzq1eo">
                  {subAgent}
                </MenuItem>)}
            </Select>
            {errors.name && <FormHelperText data-id="element-2278" data-mp="1buzr5k5so9fq">{errors.name}</FormHelperText>}
          </FormControl>
          <TextField fullWidth margin="normal" label="Description" name="description" value={formData.description} onChange={handleChange} error={!!errors.description} helperText={errors.description} multiline rows={2} data-id="element-2279" data-mp="1pvsqci71jsp6" />
          <TextField fullWidth margin="normal" label="Head Office Address" name="headOfficeAddress" value={formData.headOfficeAddress} onChange={handleChange} error={!!errors.headOfficeAddress} helperText={errors.headOfficeAddress} required multiline rows={2} data-id="element-2280" data-mp="t370hwe770b6" />
          <TextField fullWidth margin="normal" label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={handleChange} error={!!errors.contactPerson} helperText={errors.contactPerson} required data-id="element-2281" data-mp="9fgbz41ncatm" />
          {/* Pakistan Phone Input */}
          <TextField fullWidth margin="normal" label="Contact Phone" name="contactPhone" value={formData.contactPhone} onChange={handleChange} onFocus={handlePhoneFocus} onBlur={handlePhoneBlur} error={!!errors.contactPhone} helperText={errors.contactPhone || PAKISTAN_PHONE_FORMAT.phoneFormat} required placeholder={PAKISTAN_PHONE_FORMAT.phoneFormat} InputProps={{
          startAdornment: phoneFieldFocused ? <InputAdornment position="start" data-id="element-2283" data-mp="3fxt26mgfbzq3">
                  <Typography sx={{
              mr: 0.5
            }} data-id="element-2284" data-mp="14cip0mmu9j4p">
                    {PAKISTAN_PHONE_FORMAT.flag}
                  </Typography>
                </InputAdornment> : null
        }} sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0EA5E9',
              boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
            }
          }
        }} data-id="element-2282" data-mp="2o7n4os3qsaud" />
          <TextField fullWidth margin="normal" label="Contact Email" name="contactEmail" type="email" value={formData.contactEmail} onChange={handleChange} error={!!errors.contactEmail} helperText={errors.contactEmail} required placeholder="example@domain.com" data-id="element-2285" data-mp="7nbbo1wdigbv" />
          {/* IBAN Settlement Account Field Group */}
          <Box sx={{
          mt: 3,
          mb: 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: isMobile ? 2 : 1
        }} data-id="element-2286" data-mp="pop69dckxugb">
            <TextField fullWidth label="BIPL Settlement Account (IBAN)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount || 'Enter a valid IBAN'} placeholder="e.g., PK36BIPL0000000000000000" required disabled={isFetchingTitle} InputProps={{
            sx: {
              textTransform: 'uppercase'
            }
          }} sx={{
            flex: isMobile ? 1 : 3,
            width: isMobile ? '100%' : 'auto',
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0EA5E9',
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              }
            }
          }} data-id="element-2287" data-mp="kp2iwb31exts" />
            <Tooltip title="Validate and fetch account title" data-id="element-2288" data-mp="b3t7692cl59h">
              <span data-id="element-2289" data-mp="63oqoj1fwaj9">
                <Button variant="outlined" disabled={!ibanValid || isFetchingTitle} onClick={handleFetchTitle} sx={{
                minWidth: '120px',
                width: isMobile ? '100%' : 'auto',
                height: '56px',
                borderColor: '#D1D5DB',
                color: '#0EA5E9',
                '&:hover': {
                  borderColor: '#0EA5E9',
                  backgroundColor: 'rgba(14, 165, 233, 0.04)',
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  borderColor: '#E5E7EB'
                }
              }} aria-live="polite" data-id="element-2290" data-mp="635ezk2duido">
                  {isFetchingTitle ? <>
                      <CircularProgress size={16} sx={{
                    mr: 1,
                    color: '#0EA5E9'
                  }} data-id="element-2291" data-mp="1ftqefv2xoebe" />
                      Fetching...
                    </> : 'Fetch Title'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          {/* Fetch error message */}
          {fetchError && <Fade in={!!fetchError} data-id="element-2292" data-mp="1ywigmea8y71u">
              <Alert severity="error" icon={<AlertCircleIcon size={18} data-id="element-2294" data-mp="1uwhyaik11bpi" />} sx={{
            mb: 2
          }} onClose={() => setFetchError(null)} data-id="element-2293" data-mp="1yvh21zu0v2fd">
                {fetchError}
              </Alert>
            </Fade>}
          {/* Account Title Field (shown only after successful fetch) */}
          {showAccountTitle && <Fade in={showAccountTitle} data-id="element-2295" data-mp="2dlolahrusph4">
              <TextField fullWidth margin="normal" label="Account Title" value={formData.accountTitle || ''} disabled InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end" data-id="element-2297" data-mp="x6n1qdxxf50j">
                      <CheckCircleIcon size={18} color="#10B981" data-id="element-2298" data-mp="2x9o99hy0rfce" />
                    </InputAdornment>
          }} sx={{
            mb: 2,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#0F172A',
              fontWeight: 500
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D5DB',
              borderStyle: 'dashed'
            }
          }} data-id="element-2296" data-mp="2dkd85rciq5dc" />
            </Fade>}
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-2299" data-mp="3691j4bq225xn">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2300" data-mp="2scfoylrrcc7">
          <Button variant="outlined" onClick={onClose} data-id="element-2301" data-mp="1sa97sog27wgw">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-2302" data-mp="1nczlvirvq2xt">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default SubAgentFormDrawer;